import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowBack, Description, GetApp, CheckCircle, ChevronRight, BarChart, MenuBook, Image as ImageIcon, AutoAwesome, PlayArrow, Pause, Delete, Menu as MenuIcon } from '@mui/icons-material';
import { projectsService } from '../services/projects';
import { demosService } from '../services/demos';
import { voicesService } from '../services/voices';
import { ProjectResponse, AnalysisResponse, BibleResponse, CharacterBreakdownResponse, VoiceResponse, CharacterVoiceResponse } from '../types';
import { useAuthStore } from '../store/authStore';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import AudioPlayer from '../components/AudioPlayer';
import Navbar from '../components/Navbar';
import { formatContentForDisplay, cleanMarkdown } from '../utils/textFormatter';
import { parseCharacterBreakdown, CharacterInfo } from '../utils/characterBreakdownParser';
import { parseScoresFromPuanlama, parseGenelPuan } from '../utils/scoreParser';
import {
  exportToExcel,
  exportLocationBreakdownToExcel,
  exportLocationBreakdownToPDF,
} from '../utils/exportUtils';
import RadarChart from '../components/RadarChart';
import ThemeToggle from '../components/ThemeToggle';
import { ExpandMore, ExpandLess, Close } from '@mui/icons-material';
import { jsPDF } from 'jspdf';

type MenuSection = 'overview' | 'report' | 'puanlama' | 'characters' | 'sequential_breakdown' | 'locations';

interface ProjectDetailPageProps {
  demoMode?: boolean;
}

const ProjectDetailPage: React.FC<ProjectDetailPageProps> = ({ demoMode = false }) => {
  const { id } = useParams<{ id: string }>();
  const [project, setProject] = useState<ProjectResponse | null>(null);
  const [analysis, setAnalysis] = useState<AnalysisResponse | null>(null);
  const [bible, setBible] = useState<BibleResponse | null>(null);
  const [characterBreakdown, setCharacterBreakdown] = useState<CharacterBreakdownResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [loadingBible, setLoadingBible] = useState(false);
  const [loadingCharacter, setLoadingCharacter] = useState(false);
  const [locationBreakdown, setLocationBreakdown] = useState<string | null>(null);
  const [loadingLocation, setLoadingLocation] = useState(false);
  const [generatingLocation, setGeneratingLocation] = useState(false);
  const [loadingScene, setLoadingScene] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [generatingBible, setGeneratingBible] = useState(false);
  const [generatingBreakdown, setGeneratingBreakdown] = useState(false);
  const [generatingScene, setGeneratingScene] = useState(false);
  const [sceneTable, setSceneTable] = useState<{ scene_count: number; scenes: any[] } | null>(null);
  const [sequentialBreakdownTsv, setSequentialBreakdownTsv] = useState<string | null>(null);
  const [loadingSequentialBreakdown, setLoadingSequentialBreakdown] = useState(false);
  const [generatingSequentialBreakdown, setGeneratingSequentialBreakdown] = useState(false);
  const [error, setError] = useState<string>('');
  const [activeMenu, setActiveMenu] = useState<MenuSection>('overview');
  const [activeSection, setActiveSection] = useState<string>('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [expandedCharacters, setExpandedCharacters] = useState<Set<string>>(new Set());
  const [expandedCharacterInteractions, setExpandedCharacterInteractions] = useState<Set<string>>(new Set());
  const [expandedCharacterReplicas, setExpandedCharacterReplicas] = useState<Set<string>>(new Set());
  const [selectedCharacter, setSelectedCharacter] = useState<CharacterInfo | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [showProcessing, setShowProcessing] = useState(false);
  const [processingText, setProcessingText] = useState('');
  const sectionRefs = useRef<{ [key: string]: HTMLElement | null }>({});
  const [showSuccessModal, setShowSuccessModal] = useState<{
    visible: boolean;
    title: string;
    message: string;
    targetMenu?: MenuSection;
  }>({ visible: false, title: '', message: '', targetMenu: undefined });
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const { theme, toggleTheme } = useTheme();
  const { t, language } = useLanguage();

  // Audio tab state
  const [voices, setVoices] = useState<VoiceResponse[]>([]);
  const [characters, setCharacters] = useState<any[]>([]);
  const [characterVoices, setCharacterVoices] = useState<CharacterVoiceResponse[]>([]);
  const [loadingVoices, setLoadingVoices] = useState(false);
  const [loadingCharacters, setLoadingCharacters] = useState(false);
  const [synthesizing, setSynthesizing] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  // Voice IDs for audiobooks (fixed) - mapped by language code
  const VOICE_IDS: { [key: string]: string } = {
    'tr': "IuRRIAcbQK5AQk1XevPj", // Turkish
    'en': "m8PfgWULna67DKNOKhKR", // English
    'it': "o5tUAYEqld5GJZ1Lv8uC", // Italian
    'es': "ijblc1yMu3xXUhhM5ojE", // Spanish
    'ar': "JjTirzdD7T3GMLkwdd3a", // Arabic
    'de': "oYuK6X6xL9cwJKfgStee", // German (also used as default for other languages)
    'fr': "oYuK6X6xL9cwJKfgStee", // French
    'ru': "oYuK6X6xL9cwJKfgStee", // Russian
  };
  
  // Helper function to get voice ID based on report language
  const getVoiceIdByLanguage = (reportLanguage?: string): string => {
    if (!reportLanguage) return VOICE_IDS['tr']; // Default to Turkish
    
    // Normalize language code (e.g., "English" -> "en", "Türkçe" -> "tr")
    const normalizedLang = reportLanguage.toLowerCase();
    
    // Try exact match first
    if (VOICE_IDS[normalizedLang]) {
      return VOICE_IDS[normalizedLang];
    }
    
    // Try matching by language name
    const langMap: { [key: string]: string } = {
      'türkçe': 'tr',
      'turkish': 'tr',
      'english': 'en',
      'italiano': 'it',
      'italian': 'it',
      'español': 'es',
      'spanish': 'es',
      'العربية': 'ar',
      'arabic': 'ar',
      'deutsch': 'de',
      'german': 'de',
      'français': 'fr',
      'french': 'fr',
      'русский': 'ru',
      'russian': 'ru',
    };
    
    const mappedLang = langMap[normalizedLang];
    if (mappedLang && VOICE_IDS[mappedLang]) {
      return VOICE_IDS[mappedLang];
    }
    
    // Default to German voice for other languages
    return VOICE_IDS['de'];
  };
  const loadAnalysis = useCallback(async (projectId: number) => {
    setLoadingAnalysis(true);
    try {
      const data = demoMode
        ? await demosService.getDemoAnalysis(projectId)
        : await projectsService.getAnalysis(projectId);
      setAnalysis(data);
      if (data.sections && Object.keys(data.sections).length > 0) {
        setActiveSection(Object.keys(data.sections)[0]);
      }
    } catch (err: any) {
      console.error('Failed to load analysis:', err);
    } finally {
      setLoadingAnalysis(false);
    }
  }, [demoMode]);

  const loadBible = useCallback(async (projectId: number) => {
    if (demoMode) return; // demos API has no bible
    setLoadingBible(true);
    try {
      const data = await projectsService.getBible(projectId);
      setBible(data);
    } catch (err: any) {
      console.error('Failed to load bible:', err);
    } finally {
      setLoadingBible(false);
    }
  }, [demoMode]);

  const loadCharacterBreakdown = useCallback(async (projectId: number) => {
    setLoadingCharacter(true);
    try {
      const data = demoMode
        ? await demosService.getDemoCharacterBreakdown(projectId)
        : await projectsService.getCharacterBreakdown(projectId);
      setCharacterBreakdown({
        project_id: data.project_id,
        breakdown_content: data.breakdown_content ?? '',
      });
    } catch (err: any) {
      console.error('Failed to load character breakdown:', err);
      setCharacterBreakdown(null);
    } finally {
      setLoadingCharacter(false);
    }
  }, [demoMode]);

  const loadSequentialBreakdown = useCallback(async (projectId: number) => {
    setLoadingSequentialBreakdown(true);
    try {
      const data = demoMode
        ? await demosService.getDemoSequentialBreakdown(projectId)
        : await projectsService.getSequentialBreakdown(projectId);
      setSequentialBreakdownTsv(data.tsv_content ?? null);
    } catch (err: any) {
      console.error('Failed to load sequential breakdown:', err);
      setSequentialBreakdownTsv(null);
    } finally {
      setLoadingSequentialBreakdown(false);
    }
  }, [demoMode]);

  const loadLocationBreakdown = useCallback(async (projectId: number) => {
    setLoadingLocation(true);
    try {
      const data = await projectsService.getLocationBreakdown(projectId);
      setLocationBreakdown(data.breakdown_content ?? null);
    } catch (err: any) {
      console.error('Failed to load location breakdown:', err);
      setLocationBreakdown(null);
    } finally {
      setLoadingLocation(false);
    }
  }, []);

  const loadProject = useCallback(async (projectId: number, skipLoadingState: boolean = false) => {
    if (!skipLoadingState) {
      setLoading(true);
    }
    setError('');
    try {
      const data = demoMode
        ? await demosService.getDemoProject(projectId)
        : await projectsService.getProject(projectId);
      const breakdownValue = data.generate_character_breakdown as any;
      const normalizedBreakdown = breakdownValue === true || breakdownValue === 1 || breakdownValue === '1';
      const seqValue = (data as any).generate_sequential_breakdown as any;
      const normalizedSequential = seqValue === true || seqValue === 1 || seqValue === '1';
      const normalizedData = {
        ...data,
        generate_character_breakdown: normalizedBreakdown,
        generate_sequential_breakdown: normalizedSequential,
        generate_location_breakdown: !!(data as any).generate_location_breakdown,
      };
      setProject(normalizedData as ProjectResponse);
      if (normalizedData.generate_report) {
        loadAnalysis(projectId).catch(err => console.error('Failed to load analysis:', err));
      }
      if (!demoMode && normalizedData.generate_bible) {
        loadBible(projectId).catch(err => console.error('Failed to load bible:', err));
      }
      if (normalizedData.generate_character_breakdown) {
        loadCharacterBreakdown(projectId).catch(err => console.error('Failed to load character breakdown:', err));
      }
      if (normalizedData.generate_sequential_breakdown) {
        loadSequentialBreakdown(projectId).catch(err => console.error('Failed to load sequential breakdown:', err));
      }
      if (normalizedData.generate_location_breakdown) {
        loadLocationBreakdown(projectId).catch(err => console.error('Failed to load location breakdown:', err));
      }
    } catch (err: any) {
      console.error('Failed to load project:', err);
      setError(err?.response?.data?.detail || 'Proje yüklenemedi');
    } finally {
      if (!skipLoadingState) {
        setLoading(false);
      }
    }
  }, [demoMode, loadAnalysis, loadBible, loadCharacterBreakdown, loadSequentialBreakdown]);

  useEffect(() => {
    if (!demoMode && !isAuthenticated) {
      navigate('/login');
      return;
    }
    if (id) {
      loadProject(parseInt(id));
    }
  }, [id, demoMode, isAuthenticated, navigate, loadProject]);

  // Set initial active section when analysis loads
  useEffect(() => {
    if (activeMenu === 'report' && analysis?.sections && Object.keys(analysis.sections).length > 0 && !activeSection) {
      const firstSection = Object.keys(analysis.sections)[0];
      setActiveSection(firstSection);
    }
  }, [activeMenu, analysis?.sections, activeSection]);

  // Intersection Observer for tracking visible sections
  useEffect(() => {
    if (activeMenu !== 'report' || !analysis?.sections) return;

    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -60% 0px',
      threshold: 0
    };

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const sectionId = entry.target.getAttribute('data-section-id');
          if (sectionId) {
            setActiveSection(sectionId);
          }
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    // Observe all section elements
    Object.keys(analysis.sections).forEach(sectionTitle => {
      const element = sectionRefs.current[sectionTitle];
      if (element) {
        observer.observe(element);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [activeMenu, analysis?.sections]);

  const loadSceneTable = async (projectId: number) => {
    setLoadingScene(true);
    try {
      const data = await projectsService.getSceneTable(projectId);
      setSceneTable(data);
    } catch (err: any) {
      console.error('Failed to load scene table:', err);
      if (err?.response?.status !== 404) {
        setError('Sahne tablosu yüklenemedi');
      }
    } finally {
      setLoadingScene(false);
    }
  };

  const handleGenerateLocationBreakdown = async () => {
    if (!project) return;
    setGeneratingLocation(true);
    setShowProcessing(true);
    setProcessingText(language === 'tr' ? 'Mekan dökümü kuyruğa alınıyor...' : 'Queueing location breakdown...');

    /**
     * Async (Celery) flow:
     *  1. POST returns 202 + task_id immediately.
     *  2. Poll /tasks/{task_id}/status until SUCCESS or FAILURE (up to 5 min).
     *  3. On SUCCESS, fetch the saved breakdown via GET /location-breakdown.
     *
     * Backwards compatible: if the backend still runs the synchronous
     * version (no task_id in the response), use the inline content.
     * If the proxy returns 502/504 (synchronous backend timing out at the
     * Vercel edge), fall back to polling /location-breakdown directly.
     */
    try {
      const result = await projectsService.generateLocationBreakdown(project.id);

      // Legacy sync response — content already in the body
      if (result.breakdown_content) {
        setLocationBreakdown(result.breakdown_content);
        return;
      }

      // Async response — poll the task status, then fetch the saved breakdown.
      // The new scene-by-scene TSV prompt is verbose (13 columns × N scenes),
      // and a long episode/script can keep the AI provider busy for several
      // minutes. We therefore wait up to 15 minutes — plenty of headroom for
      // realistic scripts — and we ALSO race the task-status check against a
      // direct DB poll, so if the worker finished but Celery's result backend
      // is slow to flip the state, we still pick up the saved content fast.
      if (result.task_id) {
        const TASK_DEADLINE_MS = 15 * 60 * 1000; // 15 min
        const POLL_INTERVAL_MS = 5000;
        const taskDeadline = Date.now() + TASK_DEADLINE_MS;
        const startedAt = Date.now();
        let lastError: string | null = null;

        const beforeContent = locationBreakdown;

        setProcessingText(language === 'tr' ? 'Mekan dökümü hazırlanıyor (3-5 dk sürebilir)...' : 'Preparing location breakdown (3-5 min)…');

        while (Date.now() < taskDeadline) {
          await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));

          // 1) Direct DB check — wins if the worker already wrote the row
          //    (e.g. inline post-analysis hook in the upload flow).
          try {
            const fresh = await projectsService.getLocationBreakdown(project.id);
            if (fresh.breakdown_content && fresh.breakdown_content !== beforeContent) {
              setLocationBreakdown(fresh.breakdown_content);
              return;
            }
          } catch {
            // ignore intermittent errors
          }

          // 2) Task status — gives us live progress + final SUCCESS/FAILURE.
          try {
            const status = await projectsService.getTaskStatus(result.task_id);
            if (status.status) {
              const elapsed = Math.round((Date.now() - startedAt) / 1000);
              setProcessingText(`${status.status} (${elapsed}s)`);
            }

            if (status.state === 'SUCCESS') {
              const fresh = await projectsService.getLocationBreakdown(project.id);
              if (fresh.breakdown_content) {
                setLocationBreakdown(fresh.breakdown_content);
                return;
              }
              // SUCCESS without content — extremely rare race; keep polling DB.
              continue;
            }

            if (status.state === 'FAILURE' || status.state === 'ERROR') {
              lastError = status.error || (language === 'tr' ? 'Mekan dökümü oluşturulamadı.' : 'Location breakdown failed.');
              break;
            }
          } catch (pollErr: any) {
            console.warn('Location breakdown poll failed:', pollErr);
          }
        }

        if (lastError) {
          alert(`${language === 'tr' ? 'Mekan dökümü oluşturulamadı' : 'Location breakdown failed'}: ${lastError}`);
        } else {
          alert(language === 'tr'
            ? 'Mekan dökümü uzun sürdü, arkada işlenmeye devam ediyor. Bu pencereyi kapatabilirsin — birkaç dakika sonra Mekanlar sekmesini yeniden aç, sonuç gelmiş olacak.'
            : 'Location breakdown is still running in the background. You can close this — re-open the Locations tab in a few minutes and the result will be there.');
        }
        return;
      }

      // Neither breakdown_content nor task_id — unexpected shape
      console.warn('Unexpected generateLocationBreakdown response:', result);
      alert(language === 'tr' ? 'Beklenmedik sunucu yanıtı.' : 'Unexpected server response.');
    } catch (err: any) {
      console.error('generateLocationBreakdown failed:', err, err?.response);
      const status = err?.response?.status;
      const isProxyTimeout = status === 502 || status === 504 || err?.code === 'ECONNABORTED';

      if (isProxyTimeout) {
        // Sync backend timed out at the Vercel edge — best-effort fallback:
        // poll the GET endpoint for up to 5 min in case the worker finishes.
        setProcessingText(language === 'tr' ? 'Sunucu işlemi sürdürüyor, sonuç bekleniyor...' : 'Server still processing, waiting for the result…');
        const before = locationBreakdown;
        const deadline = Date.now() + 300000;
        let found: string | null = null;
        while (Date.now() < deadline) {
          await new Promise((r) => setTimeout(r, 5000));
          try {
            const res = await projectsService.getLocationBreakdown(project.id);
            if (res.breakdown_content && res.breakdown_content !== before) {
              found = res.breakdown_content;
              break;
            }
          } catch {
            // ignore intermittent errors and keep polling
          }
        }
        if (found) {
          setLocationBreakdown(found);
          return;
        }
        alert(language === 'tr'
          ? 'Mekan dökümü oluşturulamadı: Sunucu yanıt vermedi (HTTP 502). Birkaç dakika sonra sayfayı yenileyip tekrar deneyin.'
          : 'Location breakdown failed: server did not respond (HTTP 502). Refresh the page in a few minutes and try again.');
      } else {
        const detail = err?.response?.data?.detail;
        const msg = detail
          ? `${language === 'tr' ? 'Mekan dökümü oluşturulamadı' : 'Location breakdown failed'}: ${detail}${status ? ` (HTTP ${status})` : ''}`
          : `${language === 'tr' ? 'Mekan dökümü oluşturulamadı' : 'Location breakdown failed'}: ${err?.message || (language === 'tr' ? 'bilinmeyen hata' : 'unknown error')}`;
        alert(msg);
      }
    } finally {
      setGeneratingLocation(false);
      setShowProcessing(false);
      setProcessingText('');
    }
  };

  const handleMenuChange = (menu: MenuSection) => {
    setActiveMenu(menu);
    setSidebarOpen(false);

    if (id) {
      const projectId = parseInt(id);
      if (menu === 'overview' && !analysis && project?.generate_report) {
        // Load analysis for overview to extract logline and genis_ozet
        loadAnalysis(projectId);
      } else if (menu === 'report' && !analysis && project?.generate_report) {
        loadAnalysis(projectId);
      } else if (menu === 'puanlama' && !analysis && project?.generate_report) {
        loadAnalysis(projectId);
      } else if (menu === 'characters' && !characterBreakdown) {
        // Try to load character breakdown if it exists, but don't require the flag
        loadCharacterBreakdown(projectId).catch(() => {
          // Silently fail if breakdown doesn't exist - button will be shown instead
        });
      } else if (menu === 'sequential_breakdown' && sequentialBreakdownTsv === null && !loadingSequentialBreakdown) {
        loadSequentialBreakdown(projectId).catch(() => {});
      }
    }
  };

  const toggleSection = (sectionId: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };

  const handleVoiceChange = async (characterName: string, voiceId: string) => {
    if (!id) return;
    try {
      await voicesService.setCharacterVoice(parseInt(id), {
        character_name: characterName,
        voice_id: voiceId,
      });
      const updated = await voicesService.getCharacterVoices(parseInt(id));
      setCharacterVoices(updated);
    } catch (err: any) {
      console.error('Failed to set voice:', err);
      alert('Ses atanamadı');
    }
  };

  const handleSuggestVoices = async () => {
    if (!id) return;
    try {
      const suggestions = await voicesService.suggestVoices(parseInt(id));
      for (const suggestion of suggestions) {
        await voicesService.setCharacterVoice(parseInt(id), {
          character_name: suggestion.character_name,
          voice_id: suggestion.suggested_voice_id,
        });
      }
      const updated = await voicesService.getCharacterVoices(parseInt(id));
      setCharacterVoices(updated);
      alert('AI ses önerileri uygulandı!');
    } catch (err: any) {
      console.error('Failed to suggest voices:', err);
      alert('Ses önerileri alınamadı');
    }
  };

  const handleSynthesize = async () => {
    if (!id) return;
    setSynthesizing(true);
    try {
      const voiceMap: { [key: string]: string } = {};
      let narratorVoice: string | undefined;

      for (const charVoice of characterVoices) {
        if (charVoice.voice_id) {
          voiceMap[charVoice.character_name] = charVoice.voice_id;
        }
      }

      if (voices.length > 0 && !narratorVoice) {
        narratorVoice = voices[0].id;
      }

      const blob = await voicesService.synthesizeAudio(parseInt(id), {
        character_voice_map: voiceMap,
        narrator_voice: narratorVoice,
      });

      setAudioBlob(blob);
      const url = URL.createObjectURL(blob);
      setAudioUrl(url);
    } catch (err: any) {
      console.error('Failed to synthesize audio:', err);
      alert('Seslendirme başarısız oldu');
    } finally {
      setSynthesizing(false);
    }
  };

  const handleDeleteProject = async () => {
    if (!id) return;
    
    const confirmed = window.confirm(
      t.deleteConfirmMessage.replace('{name}', project?.file_name || t.projectDetails || '')
    );
    
    if (!confirmed) return;
    
    setDeleting(true);
    try {
      await projectsService.deleteProject(parseInt(id));
      alert('Proje başarıyla silindi.');
      navigate('/dashboard');
    } catch (err: any) {
      console.error('Failed to delete project:', err);
      alert('Proje silinemedi: ' + (err?.response?.data?.detail || err?.message));
    } finally {
      setDeleting(false);
    }
  };

  const handleDownloadPuanlamaPDF = async () => {
    if (!extractedPuanlama) {
      alert('Puanlama içeriği bulunamadı');
      return;
    }

    // Helper function to convert SVG to image data URL
    const svgToImageDataUrl = (svgElement: SVGSVGElement): Promise<string> => {
      return new Promise((resolve, reject) => {
        try {
          const svgData = new XMLSerializer().serializeToString(svgElement);
          const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
          const url = URL.createObjectURL(svgBlob);
          
          const img = new Image();
          img.onload = () => {
            try {
              const canvas = document.createElement('canvas');
              const ctx = canvas.getContext('2d');
              if (!ctx) {
                URL.revokeObjectURL(url);
                reject(new Error('Could not get canvas context'));
                return;
              }
              
              // Set canvas size to match SVG viewBox (500x500)
              canvas.width = 500;
              canvas.height = 500;
              
              // Fill white background
              ctx.fillStyle = 'white';
              ctx.fillRect(0, 0, canvas.width, canvas.height);
              
              // Draw the image
              ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
              
              const dataUrl = canvas.toDataURL('image/png');
              URL.revokeObjectURL(url);
              resolve(dataUrl);
            } catch (err) {
              URL.revokeObjectURL(url);
              reject(err);
            }
          };
          img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('Failed to load SVG as image'));
          };
          img.src = url;
        } catch (err) {
          reject(err);
        }
      });
    };

    try {
      const doc = new jsPDF();
      
      let yPos = 20;
      
      // Title
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('Puanlama Analizi', 14, yPos);
      yPos += 10;
      
      // Project name
      if (project?.file_name) {
        doc.setFontSize(12);
        doc.setFont('helvetica', 'normal');
        doc.text(`Proje: ${project.file_name}`, 14, yPos);
        yPos += 8;
      }
      
      // General score
      const genelPuan = parseGenelPuan(extractedPuanlama);
      if (genelPuan !== null) {
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(`Genel Puan: ${genelPuan.toFixed(1)}/10`, 14, yPos);
        yPos += 10;
      }
      
      // Try to add radar chart as image
      let chartImageDataUrl: string | null = null;
      try {
        const radarSvg = document.querySelector('svg[viewBox="0 0 500 500"]') as SVGSVGElement;
        if (radarSvg) {
          chartImageDataUrl = await svgToImageDataUrl(radarSvg);
        }
      } catch (chartErr) {
        console.warn('Could not add radar chart to PDF:', chartErr);
        // Continue without chart
      }
      
      // Add radar chart image if available
      if (chartImageDataUrl) {
        const imgWidth = 170;
        const imgHeight = 170;
        
        if (yPos + imgHeight > 270) {
          doc.addPage();
          yPos = 20;
        }
        
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('Kategori Puanları (Radar Grafik)', 14, yPos);
        yPos += 10;
        
        doc.addImage(chartImageDataUrl, 'PNG', 14, yPos, imgWidth, imgHeight);
        yPos += imgHeight + 10;
      }
      
      // Scores table
      const scores = parseScoresFromPuanlama(extractedPuanlama);
      if (scores.length > 0) {
        if (yPos > 250) {
          doc.addPage();
          yPos = 20;
        }
        
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('Kategori Puanları', 14, yPos);
        yPos += 8;
        
        // Table headers
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('Kategori', 14, yPos);
        doc.text('Puan', 170, yPos, { align: 'right' });
        yPos += 6;
        
        // Draw line
        doc.setLineWidth(0.5);
        doc.line(14, yPos, 196, yPos);
        yPos += 5;
        
        // Table rows
        doc.setFont('helvetica', 'normal');
        scores.forEach((score) => {
          if (yPos > 270) {
            doc.addPage();
            yPos = 20;
          }
          
          const scoreValue = (score.score / 10).toFixed(1); // Convert back to 0-10 scale
          doc.text(score.label, 14, yPos);
          doc.text(`${scoreValue}/10`, 170, yPos, { align: 'right' });
          yPos += 7;
        });
        
        yPos += 5;
      }
      
      // Detailed content
      if (extractedPuanlama) {
        // Clean HTML tags and get plain text
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = formatContentForDisplay(extractedPuanlama);
        const plainText = tempDiv.textContent || tempDiv.innerText || '';
        
        // Split into lines and add to PDF
        const lines = doc.splitTextToSize(plainText, 180);
        
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        if (yPos > 250) {
          doc.addPage();
          yPos = 20;
        }
        doc.text('Detaylı Analiz', 14, yPos);
        yPos += 8;
        
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        lines.forEach((line: string) => {
          if (yPos > 280) {
            doc.addPage();
            yPos = 20;
          }
          doc.text(line, 14, yPos);
          yPos += 5;
        });
      }
      
      // Save PDF
      const fileName = project?.file_name 
        ? `${project.file_name.replace(/\.[^/.]+$/, '')}_puanlama.pdf`
        : 'puanlama.pdf';
      doc.save(fileName);
    } catch (err: any) {
      console.error('Failed to generate PDF:', err);
      alert('PDF oluşturulurken bir hata oluştu: ' + (err?.message || 'Bilinmeyen hata'));
    }
  };

  const downloadPDF = async (sectionTitle?: string) => {
    if (!id) return;
    try {
      const token = localStorage.getItem('access_token');
      // Pass token both in header and as query param for compatibility
      const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1')
        .replace(/(\\[rn])+$/g, '')
        .replace(/[\r\n\t\s]+$/g, '')
        .replace(/^\s+/g, '');
      const baseUrl = sectionTitle
        ? `${API_BASE_URL}/projects/${id}/analysis-pdf/${encodeURIComponent(sectionTitle)}`
        : `${API_BASE_URL}/projects/${id}/analysis-pdf`;
      const url = token ? `${baseUrl}?token=${encodeURIComponent(token)}` : baseUrl;
      
      console.log('Downloading PDF from:', url);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('PDF response status:', response.status, response.statusText);
      
      if (response.ok) {
        const blob = await response.blob();
        console.log('PDF blob size:', blob.size);
        
        if (blob.size === 0) {
          alert('PDF indirilemedi: Boş dosya döndü');
          return;
        }
        
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = sectionTitle 
          ? `${sectionTitle.substring(0, 50)}.pdf`
          : `${project?.file_name || 'rapor'}_tam_analiz.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(downloadUrl);
        document.body.removeChild(a);
      } else {
        const errText = await response.text();
        console.error('PDF download error:', response.status, errText);
        alert('PDF indirilemedi: ' + (errText || response.statusText) + ` (Status: ${response.status})`);
      }
    } catch (err: any) {
      console.error('Failed to download PDF:', err);
      const errorMessage = err?.message || err?.toString() || 'Bilinmeyen hata';
      alert(`PDF indirilemedi: ${errorMessage}`);
    }
  };

  // Extract logline and genis_ozet from Bölüm 1 if not in project
  // This must be before any early returns to follow React Hook rules
  const { logline: extractedLogline, genis_ozet: extractedGenisOzet, puanlamaContent: extractedPuanlama } = useMemo(() => {
    if (!analysis?.sections) return { logline: null, genis_ozet: null, puanlamaContent: null };
    
    // Find Bölüm 1 section
    const bolum1Key = Object.keys(analysis.sections).find(key => 
      key.includes('BÖLÜM 1') || key.includes('Bölüm 1') || key.includes('TEMEL BİLGİLER')
    );
    
    let extractedLogline = null;
    let extractedGenisOzet = null;
    
    if (bolum1Key) {
      const bolum1Content = analysis.sections[bolum1Key];
      
      // Extract Logline - pattern matches "Logline:" followed by optional blank line and content
      // Stops at next heading like "Yazar Profili:", "Dizi Formatı:", "Tür:", "Geniş Özet:", etc.
      const loglinePatterns = [
        /Logline:\s*\n\s*([\s\S]*?)(?=\n\s*(?:Yazar Profili|Dizi Formatı|Tür\s*\(|Geniş Özet|BÖLÜM|GENEL|Çıkış|$))/i,
        /Logline:\s*([\s\S]*?)(?=\n\s*(?:Yazar Profili|Dizi Formatı|Tür\s*\(|Geniş Özet|BÖLÜM|GENEL|Çıkış|$))/i,
        /Logline\s*\(Dizi için\):\s*\n\s*([\s\S]*?)(?=\n\s*(?:Yazar Profili|Dizi Formatı|Tür\s*\(|Geniş Özet|BÖLÜM|GENEL|Çıkış|$))/i,
        /•\s*Logline:\s*\n\s*([\s\S]*?)(?=\n\s*(?:Yazar Profili|Dizi Formatı|Tür\s*\(|Geniş Özet|BÖLÜM|GENEL|Çıkış|$))/i,
      ];
      
      for (const pattern of loglinePatterns) {
        const match = bolum1Content.match(pattern);
        if (match && match[1]) {
          let loglineText = match[1].trim();
          // Clean up any remaining markers
          loglineText = loglineText.replace(/^•\s*/, '').trim();
          // Remove if it's the error message
          if (loglineText && !loglineText.toLowerCase().includes('metinden çıkarılamadı')) {
            extractedLogline = loglineText;
            break;
          }
        }
      }
      
      // Extract Geniş Özet
      const genisOzetPatterns = [
        /Geniş Özet:\s*([\s\S]*?)(?=\n\s*•\s*Çıkış|\n\s*BÖLÜM 2:|\n\s*BÖLÜM 2|Çıkış Noktası|BÖLÜM 2:|BÖLÜM 2)/i,
        /Geniş Özet\s*\(Pilot Bölüm\):\s*([\s\S]*?)(?=\n\s*•\s*Çıkış|\n\s*BÖLÜM 2:|\n\s*BÖLÜM 2|Çıkış Noktası|BÖLÜM 2:|BÖLÜM 2)/i,
        /•\s*Geniş Özet:\s*([\s\S]*?)(?=\n\s*•\s*Çıkış|\n\s*BÖLÜM 2:|\n\s*BÖLÜM 2|Çıkış Noktası|BÖLÜM 2:|BÖLÜM 2)/i,
        /Geniş Özet:\s*([\s\S]*?)(?=\n\s*BÖLÜM)/i,
      ];
      
      for (const pattern of genisOzetPatterns) {
        const match = bolum1Content.match(pattern);
        if (match && match[1]) {
          extractedGenisOzet = match[1].trim();
          break;
        }
      }
    }
    
    // Puanlama is always BÖLÜM 8 (8th section), match by section number
    const puanlamaKey = Object.keys(analysis.sections).find(key =>
      /BÖLÜM\s*8\b/i.test(key)
    );
    
    const extractedPuanlama = puanlamaKey ? analysis.sections[puanlamaKey] : null;
    
    return { logline: extractedLogline, genis_ozet: extractedGenisOzet, puanlamaContent: extractedPuanlama };
  }, [analysis?.sections]);

  if (loading) {
    return (
        <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-gray-600 dark:text-gray-400">{t.loading}...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-red-700 dark:text-red-400">{error}</p>
          </div>
          <button
            onClick={() => (demoMode ? navigate('/demos') : navigate('/dashboard'))}
            className="px-4 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100"
          >
            {demoMode ? 'Demolara dön' : t.backToDashboard}
          </button>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="mb-4 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
            <p className="text-amber-700 dark:text-amber-400">{t.projectNotFound}</p>
          </div>
          <button
            onClick={() => (demoMode ? navigate('/demos') : navigate('/dashboard'))}
            className="px-4 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100"
          >
            {demoMode ? 'Demolara dön' : t.backToDashboard}
          </button>
        </div>
      </div>
    );
  }

  const isScript = project?.project_type === 'Senaryo' || project?.project_type === 'Dizi Senaryosu';
  const analysisSections = analysis?.sections || {};
  
  // Use project values if available, otherwise use extracted values
  const displayLogline = project?.logline || extractedLogline;
  const displayGenisOzet = project?.genis_ozet || extractedGenisOzet;

  const renderContent = () => {
    if (activeMenu === 'overview') {
      return (
        <div>
          <span className="eyebrow mb-3 block">/ {t.overview?.toUpperCase()}</span>
          <h2 className="display-h display-md mb-10">
            {language === 'tr' ? <>Senaryon, <em>kayıt altında.</em></> : <>Your script, <em>on record.</em></>}
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-hairline border border-hairline mb-10">
            <div className="bg-background p-6">
              <span className="eyebrow-mute mb-2 block">{t.generalScore?.toUpperCase()}</span>
              <span className="font-serif italic text-5xl text-accent leading-none tabular-nums">
                {(() => {
                  if (analysis?.score != null && analysis.score > 0) return analysis.score.toFixed(1);
                  if (project.score !== null && project.score !== undefined && project.score > 0) return project.score.toFixed(1);
                  const genelPuan = extractedPuanlama ? parseGenelPuan(extractedPuanlama) : null;
                  if (genelPuan !== null && genelPuan > 0) return genelPuan.toFixed(1);
                  return '—';
                })()}
                <span className="font-mono not-italic text-[12px] text-ink-mute ml-2 tracking-wider align-top">/10</span>
              </span>
            </div>
            <div className="bg-background p-6">
              <span className="eyebrow-mute mb-2 block">{t.type?.toUpperCase()}</span>
              <span className="font-serif italic text-2xl text-ink leading-snug">{project.project_type || '—'}</span>
            </div>
            <div className="bg-background p-6">
              <span className="eyebrow-mute mb-2 block">{t.format?.toUpperCase()}</span>
              <span className="font-serif italic text-2xl text-ink leading-snug">{project.format_type || '—'}</span>
            </div>
          </div>

          {displayLogline && (
            <div className="mb-10">
              <span className="eyebrow mb-3 block">/ {t.logline?.toUpperCase()}</span>
              <div
                className="font-prose text-[18px] leading-relaxed text-ink"
                dangerouslySetInnerHTML={{ __html: formatContentForDisplay(displayLogline) }}
              />
            </div>
          )}

          {displayGenisOzet && (
            <div className="mb-10 pt-10 border-t border-hairline">
              <span className="eyebrow mb-3 block">/ {t.genisOzet?.toUpperCase()}</span>
              <div
                className="font-prose text-[16px] leading-relaxed text-ink-dim"
                dangerouslySetInnerHTML={{ __html: formatContentForDisplay(displayGenisOzet) }}
              />
            </div>
          )}

          {/* Action Buttons - Karakter Dökümü (gizli demo modunda) */}
          {!demoMode && (
            <div className="pt-10 border-t border-hairline">
              <span className="eyebrow mb-4 block">/ {language === 'tr' ? 'EK İŞLEMLER' : 'EXTRA ACTIONS'}</span>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  onClick={async () => {
                    if (!id) return;
                    setGeneratingBreakdown(true);
                    try {
                      const response = await projectsService.generateCharacterBreakdown(parseInt(id));
                      console.log('Character breakdown response:', response);
                      
                      // Update project state immediately with the flag from response
                      if (response.generate_character_breakdown !== undefined) {
                        const breakdownValue = response.generate_character_breakdown as any;
                        const normalizedBreakdown = breakdownValue === true || breakdownValue === 1 || breakdownValue === '1';
                        console.log('Updating project state with breakdown flag:', normalizedBreakdown);
                        setProject(prevProject => {
                          if (!prevProject) return prevProject;
                          return {
                            ...prevProject,
                            generate_character_breakdown: normalizedBreakdown
                          };
                        });
                      }
                      
                      // Reload project to ensure all data is fresh (skip loading state to avoid flicker)
                      const projectId = parseInt(id);
                      await loadProject(projectId, true);
                      
                      // Force a delay to ensure state is updated and React re-renders
                      await new Promise(resolve => setTimeout(resolve, 300));
                      
                      // Reload character breakdown
                      await loadCharacterBreakdown(projectId);
                      
                      // Force menu update to show characters tab - this will trigger a re-render
                      setActiveMenu('characters');
                      
                      // Use a small delay before showing success modal to ensure menu is visible
                      setTimeout(() => {
                        setShowSuccessModal({
                          visible: true,
                          title: t.characterBreakdownCreated,
                          message: t.characterBreakdownCreatedMessage,
                          targetMenu: 'characters',
                        });
                      }, 100);
                    } catch (err: any) {
                      setError(err?.response?.data?.detail || t.characterBreakdownFailed);
                    } finally {
                      setGeneratingBreakdown(false);
                    }
                  }}
                  disabled={generatingBreakdown}
                  className="btn-ghost justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <MenuBook sx={{ fontSize: 14 }} />
                  {generatingBreakdown ? t.generating : t.generateCharacterBreakdownButton}
                </button>
              </div>
            </div>
          )}
        </div>
      );
    }

    if (activeMenu === 'puanlama') {
      if (loadingAnalysis) {
        return (
          <div className="flex justify-center items-center py-16">
            <div className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600 dark:text-gray-400'}>{t.loadingAnalysis}</div>
          </div>
        );
      }

      if (!extractedPuanlama) {
        console.log('extractedPuanlama is null/undefined');
        console.log('analysis?.sections keys:', analysis?.sections ? Object.keys(analysis.sections) : 'no sections');
        return (
          <div className={`rounded-lg p-4 border ${theme === 'dark' ? 'bg-gray-950 border-gray-600/40' : 'bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700'}`}>
            <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700 dark:text-gray-300'}>Puanlama bölümü henüz oluşturulmamış.</p>
          </div>
        );
      }

      // Prefer structured scoring_categories from API, fallback to text parsing
      let scores: { label: string; score: number; description?: string }[] = [];

      if (analysis?.scoring_categories && analysis.scoring_categories.length > 0) {
        // Use structured data from backend (0-10 scale → convert to 0-100)
        scores = analysis.scoring_categories.map(cat => ({
          label: cat.label,
          score: cat.score * 10,
          description: cat.description || undefined,
        }));
      } else {
        // Fallback: parse from raw text
        scores = parseScoresFromPuanlama(extractedPuanlama);
      }

      return (
        <div className="w-full">
          <span className="eyebrow mb-3 block">/ {t.scoring?.toUpperCase()}</span>
          <h2 className="display-h display-md mb-3">
            {language === 'tr' ? <>Yedi <em>eksen.</em></> : <>Seven <em>axes.</em></>}
          </h2>
          <p className="font-prose text-[15px] text-ink-dim leading-relaxed mb-10 max-w-2xl">
            {language === 'tr' ? 'Puanlama analizi — her eksen kendi mantığıyla.' : 'Score analysis — each axis with its own logic.'}
          </p>

          {scores.length > 0 ? (
            <div className="border border-hairline p-6 lg:p-10 mb-10">
              <RadarChart data={scores} />
            </div>
          ) : (
            <div className="border border-signal-yellow/40 bg-signal-yellow/10 p-5 mb-10">
              <span className="eyebrow-mute mb-1 block text-signal-yellow">/ NOTICE</span>
              <p className="font-prose text-[14px] text-ink-dim leading-relaxed">
                {language === 'tr' ? 'Radar grafik için yeterli puan verisi bulunamadı.' : 'Not enough score data for the radar chart.'}
              </p>
            </div>
          )}

          <div className="border border-hairline p-6 lg:p-10">
            <div
              className="analysis-content"
              id="puanlama-detailed-content"
              dangerouslySetInnerHTML={{ __html: formatContentForDisplay(extractedPuanlama) }}
            />
          </div>
        </div>
      );
    }

    if (activeMenu === 'report') {
      if (loadingAnalysis) {
        return (
          <div className="flex justify-center items-center py-16">
            <div className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600 dark:text-gray-400'}>{t.loadingAnalysis}</div>
          </div>
        );
      }

      if (!analysis || !analysis.sections || Object.keys(analysis.sections).length === 0) {
        return (
          <div className={`rounded-lg p-4 border ${theme === 'dark' ? 'bg-gray-950 border-gray-600/40' : 'bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700'}`}>
            <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700 dark:text-gray-300'}>{t.noAnalysisSections}</p>
          </div>
        );
      }

      const sectionTitles = Object.keys(analysisSections);

      return (
        <div>
          {/* Header */}
          <div className="flex items-end justify-between mb-12 pb-6 border-b border-hairline">
            <div>
              <span className="eyebrow mb-3 block">/ {t.report?.toUpperCase()}</span>
              <h2 className="display-h display-md mb-2">
                {language === 'tr' ? <>{sectionTitles.length} <em>bölüm.</em></> : <>{sectionTitles.length} <em>chapters.</em></>}
              </h2>
            </div>
            <button onClick={() => downloadPDF()} className="btn-accent">
              <GetApp sx={{ fontSize: 14 }} />
              PDF
            </button>
          </div>

          {/* Sections */}
          <div className="space-y-20">
            {sectionTitles.map((sectionTitle, index) => {
              const sectionContent = analysisSections[sectionTitle] || '';
              const sectionId = `section-${index}`;
              const sectionNumber = index + 1;

              return (
                <article
                  key={sectionTitle}
                  ref={(el) => { sectionRefs.current[sectionTitle] = el; }}
                  data-section-id={sectionTitle}
                  id={sectionId}
                  className="scroll-mt-24"
                >
                  {/* Section Header */}
                  <div className="flex items-start justify-between mb-8 pb-6 border-b border-hairline">
                    <div className="flex-1 min-w-0">
                      <span className="eyebrow mb-3 block">
                        / {`${language === 'tr' ? 'BÖLÜM' : 'CH.'}`} {String(sectionNumber).padStart(2, '0')}
                      </span>
                      <h3 className="font-serif text-3xl md:text-4xl text-ink leading-tight tracking-[-0.02em]">
                        {sectionTitle.replace(/^BÖLÜM\s*\d+:\s*/i, '').replace(/^CHAPTER\s*\d+:\s*/i, '')}
                      </h3>
                    </div>
                    <button
                      onClick={() => downloadPDF(sectionTitle)}
                      className="ml-4 w-9 h-9 flex items-center justify-center text-ink-mute hover:text-accent border border-hairline hover:border-accent transition-colors shrink-0"
                      title={language === 'tr' ? 'Bu bölümü PDF olarak indir' : 'Download this chapter as PDF'}
                    >
                      <GetApp sx={{ fontSize: 16 }} />
                    </button>
                  </div>

                  {/* Section Content */}
                  <div
                    className="analysis-content"
                    dangerouslySetInnerHTML={{ __html: formatContentForDisplay(sectionContent) }}
                  />
                </article>
              );
            })}
          </div>
        </div>
      );
    }


    if (activeMenu === 'characters') {
      if (loadingCharacter) {
        return (
          <div className="py-16">
            <span className="eyebrow-mute">{t.loadingAnalysis?.toUpperCase() || 'LOADING…'}</span>
          </div>
        );
      }

      if (!characterBreakdown) {
        return (
          <div>
            <span className="eyebrow mb-3 block">/ {t.characters?.toUpperCase()}</span>
            <h2 className="display-h display-md mb-4">
              {language === 'tr' ? <>Henüz <em>karakter</em> dökümü yok.</> : <>No <em>character</em> breakdown yet.</>}
            </h2>
            <p className="font-prose text-[15px] text-ink-dim leading-relaxed mb-8 max-w-2xl">
              {t.characterBreakdownNotCreated}
            </p>
            {demoMode ? (
              <span className="eyebrow-mute">DEMO — SALT OKUNUR</span>
            ) : (
              <button
                onClick={async () => {
                  if (!id) return;
                  setGeneratingBreakdown(true);
                  setShowProcessing(true);
                  setProcessingText(t.generatingCharacterBreakdown);
                  try {
                    const response = await projectsService.generateCharacterBreakdown(parseInt(id));
                    if (response.generate_character_breakdown !== undefined) {
                      const breakdownValue = response.generate_character_breakdown as any;
                      const normalizedBreakdown = breakdownValue === true || breakdownValue === 1 || breakdownValue === '1';
                      setProject(prevProject => {
                        if (!prevProject) return prevProject;
                        return { ...prevProject, generate_character_breakdown: normalizedBreakdown };
                      });
                    }
                    await loadProject(parseInt(id), true);
                    await loadCharacterBreakdown(parseInt(id));
                    setShowSuccessModal({
                      visible: true,
                      title: t.characterBreakdownCreated,
                      message: t.characterBreakdownCreatedMessage,
                      targetMenu: 'characters',
                    });
                  } catch (err: any) {
                    console.error('Failed to generate breakdown:', err);
                    alert(t.characterBreakdownFailed + ': ' + (err?.response?.data?.detail || err?.message));
                  } finally {
                    setGeneratingBreakdown(false);
                    setShowProcessing(false);
                    setProcessingText('');
                  }
                }}
                disabled={generatingBreakdown}
                className="btn-accent disabled:opacity-50"
              >
                {generatingBreakdown ? t.generating : t.generateCharacterBreakdownButton}
                {!generatingBreakdown && <span aria-hidden>→</span>}
              </button>
            )}
          </div>
        );
      }

      // Parse character breakdown
      let characters: CharacterInfo[] = [];
      try {
        if (characterBreakdown?.breakdown_content) {
          console.log('Character breakdown content length:', characterBreakdown.breakdown_content.length);
          console.log('Character breakdown content preview (first 2000 chars):', characterBreakdown.breakdown_content.substring(0, 2000));
          characters = parseCharacterBreakdown(characterBreakdown.breakdown_content);
          console.log('Parsed characters:', characters.length);
          characters.forEach(char => {
            console.log(`Character: ${char.name}, Replicas: ${char.replicas.length}`);
            if (char.replicas.length > 0) {
              console.log(`  First replica:`, char.replicas[0]);
            }
          });
        } else {
          console.warn('Character breakdown content is missing');
        }
      } catch (err) {
        console.error('Failed to parse character breakdown:', err);
        characters = [];
      }

      const toggleCharacter = (characterName: string) => {
        const newExpanded = new Set(expandedCharacters);
        if (newExpanded.has(characterName)) {
          newExpanded.delete(characterName);
        } else {
          newExpanded.add(characterName);
        }
        setExpandedCharacters(newExpanded);
      };

      const toggleCharacterInteractions = (characterName: string) => {
        const newExpanded = new Set(expandedCharacterInteractions);
        if (newExpanded.has(characterName)) {
          newExpanded.delete(characterName);
        } else {
          newExpanded.add(characterName);
        }
        setExpandedCharacterInteractions(newExpanded);
      };

      const toggleCharacterReplicas = (characterName: string) => {
        const newExpanded = new Set(expandedCharacterReplicas);
        if (newExpanded.has(characterName)) {
          newExpanded.delete(characterName);
        } else {
          newExpanded.add(characterName);
        }
        setExpandedCharacterReplicas(newExpanded);
      };

      // Get role type color based on role type
      const getRoleTypeColor = (roleType: string | undefined): string => {
        if (!roleType) return 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300';
        
        const role = roleType.toLowerCase();
        if (role.includes('başrol') || role.includes('ana karakter') || role.includes('protagonist')) {
          return 'bg-purple-100 dark:bg-gray-700 text-purple-700 dark:text-gray-200';
        } else if (role.includes('yan karakter') && (role.includes('önemli') || role.includes('important'))) {
          return 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200';
        } else if (role.includes('yan karakter') || role.includes('yan rol')) {
          return 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300';
        } else if (role.includes('figüran') || role.includes('figuran')) {
          return 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300';
        }
        return 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300';
      };

      const handleDownloadCharacterBreakdownPDF = async () => {
        if (!id || characters.length === 0) {
          alert('Karakter dökümü bulunamadı');
          return;
        }
        try {
          const token = localStorage.getItem('access_token');
          // Send parsed characters from frontend to backend
          const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1')
            .replace(/(\\[rn])+$/g, '')
            .replace(/[\r\n\t\s]+$/g, '')
            .replace(/^\s+/g, '');
          const baseUrl = `${API_BASE_URL}/projects/${id}/character-breakdown-pdf-from-parsed`;
          const url = token ? `${baseUrl}?token=${encodeURIComponent(token)}` : baseUrl;
          
          console.log('Sending parsed characters to backend for PDF generation:', characters.length, 'characters');
          
          const response = await fetch(url, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              characters: characters,
              project_name: project?.file_name || 'Rapor'
            })
          });
          
          console.log('PDF response status:', response.status, response.statusText);
          
          if (response.ok) {
            const blob = await response.blob();
            console.log('PDF blob size:', blob.size);
            
            if (blob.size === 0) {
              alert('PDF indirilemedi: Boş dosya döndü');
              return;
            }
            
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = project?.file_name 
              ? `${project.file_name.replace(/\.[^/.]+$/, '')}_karakter_dokumu.pdf`
              : 'karakter_dokumu.pdf';
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(downloadUrl);
            document.body.removeChild(a);
          } else {
            const errText = await response.text();
            console.error('PDF download error:', response.status, errText);
            alert('PDF indirilemedi: ' + (errText || response.statusText) + ` (Status: ${response.status})`);
          }
        } catch (err: any) {
          console.error('Failed to download PDF:', err);
          const errorMessage = err?.message || err?.toString() || 'Bilinmeyen hata';
          alert(`PDF indirilemedi: ${errorMessage}`);
        }
      };

      return (
        <div>
          <div className="flex items-end justify-between mb-12 pb-6 border-b border-hairline">
            <div>
              <span className="eyebrow mb-3 block">/ {t.characters?.toUpperCase()}</span>
              <h2 className="display-h display-md">
                {language === 'tr' ? <>{characters.length} <em>karakter.</em></> : <>{characters.length} <em>characters.</em></>}
              </h2>
            </div>
            {characters.length > 0 && (
              <button onClick={handleDownloadCharacterBreakdownPDF} className="btn-accent">
                <GetApp sx={{ fontSize: 14 }} />
                PDF
              </button>
            )}
          </div>

          {characters.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-hairline border border-hairline">
              {characters.map((character, idx) => {
                return (
                  <div
                    key={idx}
                    onClick={() => setSelectedCharacter(character)}
                    className="bg-background p-6 cursor-pointer hover:bg-accent-soft transition-colors group"
                  >
                    <div className="flex items-baseline justify-between mb-4">
                      <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-ink-mute">
                        /{String(idx + 1).padStart(2, '0')}
                      </span>
                      {character.roleType && (
                        <span className="font-mono text-[10px] tracking-[0.18em] uppercase font-bold text-accent">
                          {cleanMarkdown(character.roleType).toUpperCase()}
                        </span>
                      )}
                    </div>

                    <h3 className="font-serif italic text-2xl text-ink leading-tight mb-3 group-hover:text-accent transition-colors">
                      {character.name}
                    </h3>

                    {character.totalScenes && character.totalReplicas && (
                      <div className="font-mono text-[11px] tracking-wider uppercase text-ink-mute mb-3 tabular-nums">
                        {cleanMarkdown(character.totalScenes)} {language === 'tr' ? 'SAHNE' : 'SCENES'} · {cleanMarkdown(character.totalReplicas)} {language === 'tr' ? 'REPLİK' : 'LINES'}
                      </div>
                    )}
                    {character.shortDescription && (
                      <p className="font-prose text-[13px] text-ink-dim leading-relaxed line-clamp-3">
                        {cleanMarkdown(character.shortDescription.substring(0, 150))}
                        {character.shortDescription.length > 150 && '…'}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            // Fallback to formatted text if parsing fails
            <div className={`rounded-xl p-6 border ${theme === 'dark' ? 'bg-gray-950 border-gray-600/40' : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'}`}>
              <div
                className="text-base leading-relaxed max-w-none"
                style={{ 
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                  lineHeight: '1.75',
                  color: theme === 'dark' ? 'rgb(203, 213, 225)' : 'inherit'
                }}
                dangerouslySetInnerHTML={{
                  __html: formatContentForDisplay(characterBreakdown.breakdown_content)
                }}
              />
            </div>
          )}

          {/* Character Detail Modal */}
          {selectedCharacter && (
            <div 
              className="fixed inset-0 bg-background/85 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setSelectedCharacter(null)}
            >
              <div
                className="bg-elevated border border-hairline max-w-4xl w-full max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Modal Header */}
                <div className="sticky top-0 bg-elevated border-b border-hairline px-6 py-5 flex items-center justify-between z-10">
                  <div>
                    <span className="eyebrow-mute mb-1 block">/ {language === 'tr' ? 'KARAKTER DOSYASI' : 'CHARACTER FILE'}</span>
                    <h2 className="font-serif italic text-3xl text-ink leading-tight">
                      {selectedCharacter.name}
                    </h2>
                  </div>
                  <button
                    onClick={() => setSelectedCharacter(null)}
                    className="w-9 h-9 flex items-center justify-center text-ink-mute hover:text-ink border border-hairline hover:border-accent transition-colors shrink-0"
                  >
                    <Close sx={{ fontSize: 18 }} />
                  </button>
                </div>

                {/* Modal Content */}
                <div className="p-6 space-y-6">
                  {/* 1. Rol Tipi */}
                  {selectedCharacter.roleType && (
                    <div className="pb-4 border-b border-gray-200 dark:border-gray-700">
                      <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                        1. Rol Tipi
                      </div>
                      <div className="text-base leading-relaxed">
                        <span className={`inline-block px-3 py-1.5 text-sm font-medium rounded ${getRoleTypeColor(selectedCharacter.roleType)}`}>
                          {cleanMarkdown(selectedCharacter.roleType)}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* 2. Karakterin Tipi */}
                  {selectedCharacter.characterType && (
                    <div className="pb-4 border-b border-gray-200 dark:border-gray-700">
                      <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                        2. Karakterin Tipi
                      </div>
                      <div className="text-base text-gray-900 dark:text-white leading-relaxed">
                        {cleanMarkdown(selectedCharacter.characterType)}
                      </div>
                    </div>
                  )}

                  {/* 3. Karakterin Sahne Sayısı / Replik Sayısı */}
                  {(selectedCharacter.totalScenes || selectedCharacter.totalReplicas) && (
                    <div className="pb-4 border-b border-gray-200 dark:border-gray-700">
                      <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                        3. {t.characterSceneCount}
                      </div>
                      <div className="text-base text-gray-900 dark:text-white">
                        {selectedCharacter.totalScenes && cleanMarkdown(selectedCharacter.totalScenes)}
                        {selectedCharacter.totalScenes && selectedCharacter.totalReplicas && ' / '}
                        {selectedCharacter.totalReplicas && cleanMarkdown(selectedCharacter.totalReplicas)}
                      </div>
                    </div>
                  )}

                  {/* 4. Karakter Kısa Tanıtımı */}
                  {selectedCharacter.shortDescription && (
                    <div className="pb-4 border-b border-gray-200 dark:border-gray-700">
                      <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                        4. Karakter Kısa Tanıtımı
                      </div>
                      <div className="text-base text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">
                        {cleanMarkdown(selectedCharacter.shortDescription)}
                      </div>
                    </div>
                  )}

                  {/* İkinci Kısım: Karakter Etkileşim Dökümü (Açılır Kapanır) */}
                  {selectedCharacter.interactions && selectedCharacter.interactions.length > 0 && (
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                      <button
                        onClick={() => {
                          const newExpanded = new Set(expandedCharacterInteractions);
                          if (newExpanded.has(selectedCharacter.name)) {
                            newExpanded.delete(selectedCharacter.name);
                          } else {
                            newExpanded.add(selectedCharacter.name);
                          }
                          setExpandedCharacterInteractions(newExpanded);
                        }}
                        className="w-full flex items-center justify-between py-3 text-base font-semibold text-gray-900 dark:text-white hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                      >
                        <span>{t.characterInteractionBreakdown}</span>
                        {expandedCharacterInteractions.has(selectedCharacter.name) ? (
                          <ExpandLess style={{ width: '20px', height: '20px' }} />
                        ) : (
                          <ExpandMore style={{ width: '20px', height: '20px' }} />
                        )}
                      </button>
                      
                      {expandedCharacterInteractions.has(selectedCharacter.name) && (
                        <div className="mt-3 pl-2">
                          <ul className="space-y-2">
                            {selectedCharacter.interactions.map((interaction, i) => (
                              <li 
                                key={i}
                                className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-300 leading-relaxed"
                              >
                                {cleanMarkdown(interaction)}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Üçüncü Kısım: Replikler (Açılır Kapanır) */}
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                    <button
                      onClick={() => {
                        const newExpanded = new Set(expandedCharacterReplicas);
                        if (newExpanded.has(selectedCharacter.name)) {
                          newExpanded.delete(selectedCharacter.name);
                        } else {
                          newExpanded.add(selectedCharacter.name);
                        }
                        setExpandedCharacterReplicas(newExpanded);
                      }}
                      className="w-full flex items-center justify-between py-3 text-base font-semibold text-gray-900 dark:text-white hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                    >
                      <span>
                        {t.replicas} {selectedCharacter.replicas.length > 0 && `(${selectedCharacter.replicas.length})`}
                      </span>
                      {expandedCharacterReplicas.has(selectedCharacter.name) ? (
                        <ExpandLess style={{ width: '20px', height: '20px' }} />
                      ) : (
                        <ExpandMore style={{ width: '20px', height: '20px' }} />
                      )}
                    </button>
                    
                    {expandedCharacterReplicas.has(selectedCharacter.name) && (
                      <div className="mt-3 pl-2">
                        {selectedCharacter.replicas.length > 0 ? (
                          <div className="space-y-2 max-h-96 overflow-y-auto">
                            {selectedCharacter.replicas.map((replica, replicaIdx) => (
                              <div
                                key={replicaIdx}
                                className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
                              >
                                {replica.scene && (
                                  <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                                    {t.scene} {cleanMarkdown(replica.scene)}
                                  </div>
                                )}
                                <div className="text-sm text-gray-700 dark:text-gray-300 italic leading-relaxed">
                                  "{cleanMarkdown(replica.text)}"
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-sm text-gray-500 dark:text-gray-400 italic py-2">
                            {t.noDialogue}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }

    if (activeMenu === 'sequential_breakdown') {
      if (loadingSequentialBreakdown) {
        return (
          <div className="flex justify-center items-center py-16">
            <div className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600 dark:text-gray-400'}>{t.loadingAnalysis}</div>
          </div>
        );
      }
      if (!sequentialBreakdownTsv) {
        return (
          <div>
            <span className="eyebrow mb-3 block">/ {t.sequentialBreakdown?.toUpperCase()}</span>
            <h2 className="display-h display-md mb-4">
              {language === 'tr' ? <>Henüz <em>sıralı döküm</em> yok.</> : <>No <em>sequential breakdown</em> yet.</>}
            </h2>
            <p className="font-prose text-[15px] text-ink-dim leading-relaxed mb-8 max-w-2xl">
              {t.sequentialBreakdownNotCreated}
              {!demoMode && (language === 'tr' ? ' · 4 token ile oluşturulur.' : ' · Costs 4 tokens.')}
            </p>
            {demoMode ? (
              <span className="eyebrow-mute">DEMO — SALT OKUNUR</span>
            ) : (
                  <button
                    onClick={async () => {
                      if (!id) return;
                      setGeneratingSequentialBreakdown(true);
                      setShowProcessing(true);
                      setProcessingText('Sıralı döküm oluşturuluyor...');
                      try {
                        const response = await projectsService.generateSequentialBreakdown(parseInt(id));
                        setSequentialBreakdownTsv(response.tsv_content);
                        setProject(prev => prev ? { ...prev, generate_sequential_breakdown: true } : prev);
                        setShowSuccessModal({
                          visible: true,
                          title: t.sequentialBreakdownCreated,
                          message: t.sequentialBreakdownCreatedMessage,
                          targetMenu: 'sequential_breakdown',
                        });
                      } catch (err: any) {
                        alert(t.sequentialBreakdownFailed + ': ' + (err?.response?.data?.detail || err?.message));
                      } finally {
                        setGeneratingSequentialBreakdown(false);
                        setShowProcessing(false);
                        setProcessingText('');
                      }
                    }}
                    disabled={generatingSequentialBreakdown}
                    className="btn-accent disabled:opacity-50"
                  >
                    {generatingSequentialBreakdown ? t.generating : t.generateSequentialBreakdownButton}
                    {!generatingSequentialBreakdown && <span aria-hidden>→</span>}
                  </button>
            )}
          </div>
        );
      }
      const lines = sequentialBreakdownTsv.trim().split(/\r?\n/).filter(Boolean);
      const headers = lines[0].split('\t').map((h: string) => h.trim());
      const rows = lines.slice(1).map((line: string) => line.split('\t').map((c: string) => c.trim()));
      const excelData = rows.map((row: string[]) => {
        const obj: Record<string, string> = {};
        headers.forEach((h, i) => { obj[h] = row[i] ?? ''; });
        return obj;
      });
      const safeFilename = (project?.file_name || `project-${id}`).replace(/[^a-zA-Z0-9_-]/g, '_');
      return (
        <div>
          <div className="flex items-end justify-between mb-12 pb-6 border-b border-hairline">
            <div>
              <span className="eyebrow mb-3 block">/ {t.sequentialBreakdown?.toUpperCase()}</span>
              <h2 className="display-h display-md">
                {language === 'tr' ? <>{rows.length} <em>sahne.</em></> : <>{rows.length} <em>scenes.</em></>}
              </h2>
            </div>
            <button
              onClick={() => exportToExcel(excelData, `siralı_döküm_${safeFilename}`, 'Sıralı Döküm')}
              className="btn-accent"
            >
              <GetApp sx={{ fontSize: 14 }} />
              {t.exportExcel?.toUpperCase()}
            </button>
          </div>
          <div className="overflow-x-auto border border-hairline w-full">
            <table className="w-full min-w-[880px]">
              <thead className="bg-elevated border-b border-hairline">
                <tr>
                  {headers.map((h, i) => (
                    <th key={i} className="px-4 py-4 text-left font-mono text-[10px] tracking-[0.18em] uppercase text-accent font-bold whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, ri) => (
                  <tr key={ri} className="border-b border-hairline last:border-b-0 hover:bg-accent-soft transition-colors">
                    {row.map((cell, ci) => (
                      <td
                        key={ci}
                        className={`px-4 py-4 font-mono text-[12px] whitespace-pre-wrap min-w-[10rem] tabular-nums ${
                          ci === 0 ? 'text-accent font-bold' : 'text-ink-dim'
                        }`}
                      >
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    if (activeMenu === 'locations') {
      if (loadingLocation) {
        return (
          <div className="flex justify-center items-center py-16">
            <div className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600 dark:text-gray-400'}>Mekan dökümü yükleniyor...</div>
          </div>
        );
      }

      if (!locationBreakdown) {
        return (
          <div>
            <span className="eyebrow mb-3 block">/ {language === 'tr' ? 'MEKANLAR' : 'LOCATIONS'}</span>
            <h2 className="display-h display-md mb-4">
              {language === 'tr' ? <>Henüz <em>mekan</em> dökümü yok.</> : <>No <em>location</em> breakdown yet.</>}
            </h2>
            <p className="font-prose text-[15px] text-ink-dim leading-relaxed mb-8 max-w-2xl">
              {language === 'tr'
                ? 'Yapım ekibi için tüm mekanların tablosu — iç/dış, gün/gece, sahne sayıları, karakterler, prodüksiyon notları. 1 token.'
                : 'A full table of every location for the production crew — int/ext, day/night, scene count, characters, production notes. 1 token.'}
            </p>
            {!demoMode && (
              <button
                onClick={handleGenerateLocationBreakdown}
                disabled={generatingLocation}
                className="btn-accent disabled:opacity-50"
              >
                {generatingLocation ? (language === 'tr' ? 'OLUŞTURULUYOR…' : 'GENERATING…') : (language === 'tr' ? 'MEKAN DÖKÜMÜ OLUŞTUR' : 'GENERATE LOCATION BREAKDOWN')}
                {!generatingLocation && <span aria-hidden>→</span>}
              </button>
            )}
          </div>
        );
      }

      // Strip code fences if the model wrapped the TSV in ```tsv ... ```
      const cleanedLocationContent = locationBreakdown
        .replace(/^```[a-zA-Z]*\s*\n?/, '')
        .replace(/\n?```\s*$/, '')
        .trim();
      const locLines = cleanedLocationContent.split(/\r?\n/).filter(Boolean);
      const isTsv = locLines.length >= 2 && locLines[0].includes('\t');

      if (isTsv) {
        const locHeaders = locLines[0].split('\t').map((h: string) => h.trim());
        const locRows = locLines.slice(1).map((line: string) => line.split('\t').map((c: string) => c.trim()));
        const locExcelData = locRows.map((row: string[]) => {
          const obj: Record<string, string> = {};
          locHeaders.forEach((h, i) => { obj[h] = row[i] ?? ''; });
          return obj;
        });
        const safeFilename = (project?.file_name || `project-${id}`).replace(/[^a-zA-Z0-9_-]/g, '_');

        // Detect "Mekan Grubu" column for the new grouped-by-location format
        const groupColIdx = locHeaders.findIndex((h) => /mekan\s*grubu/i.test(h));
        const pageColIdx = locHeaders.findIndex((h) => /^sayfa$/i.test(h));
        const isGroupedFormat = groupColIdx === 0; // group column must be first

        if (isGroupedFormat) {
          // Group rows by their first-column value, preserving the order
          // the AI emitted (which the prompt requires: alphabetical by group,
          // then ascending scene number within each group).
          type Group = { name: string; rows: string[][]; pageTotal: number };
          const groups: Group[] = [];
          let currentGroup: Group | null = null;
          for (const row of locRows) {
            const groupName = row[groupColIdx] || '—';
            if (!currentGroup || currentGroup.name !== groupName) {
              currentGroup = { name: groupName, rows: [], pageTotal: 0 };
              groups.push(currentGroup);
            }
            currentGroup.rows.push(row);
            const pageStr = pageColIdx >= 0 ? row[pageColIdx] : '';
            const pageNum = parseFloat((pageStr || '0').replace(',', '.'));
            if (!isNaN(pageNum)) currentGroup.pageTotal += pageNum;
          }

          // Hide the "Mekan Grubu" column from the per-row table since it's
          // now redundant with the section header.
          const visibleHeaderIdxs = locHeaders.map((_, i) => i).filter((i) => i !== groupColIdx);
          const visibleHeaders = visibleHeaderIdxs.map((i) => locHeaders[i]);
          const sceneNoColLocalIdx = visibleHeaders.findIndex((h) => /^sahne/i.test(h));

          // Re-shape groups for the export helpers (rows projected onto
          // the visible-only column order so the export matches what's
          // on screen).
          const exportGroups = groups.map((g) => ({
            name: g.name,
            pageTotal: g.pageTotal,
            rows: g.rows.map((row) => visibleHeaderIdxs.map((origIdx) => row[origIdx] ?? '')),
          }));
          const exportTitle = `${project?.file_name || 'Senaryo'} · Mekan Dökümü`;

          return (
            <div className="w-full">
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12 pb-6 border-b border-hairline">
                <div>
                  <span className="eyebrow mb-3 block">/ {language === 'tr' ? 'MEKAN DÖKÜMÜ' : 'LOCATION BREAKDOWN'}</span>
                  <h2 className="display-h display-md">
                    {language === 'tr'
                      ? <>{locRows.length} <em>sahne</em> · {groups.length} <em>mekan</em>.</>
                      : <>{locRows.length} <em>scenes</em> · {groups.length} <em>locations</em>.</>}
                  </h2>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() =>
                      exportLocationBreakdownToExcel(
                        exportTitle,
                        visibleHeaders,
                        exportGroups,
                        `mekan_dokumu_${safeFilename}`,
                      )
                    }
                    className="btn-accent"
                  >
                    <GetApp sx={{ fontSize: 14 }} />
                    {(language === 'tr' ? 'EXCEL' : 'EXCEL')}
                  </button>
                  <button
                    onClick={() =>
                      exportLocationBreakdownToPDF(
                        exportTitle,
                        visibleHeaders,
                        exportGroups,
                        `mekan_dokumu_${safeFilename}`,
                      )
                    }
                    className="btn-ghost"
                  >
                    <GetApp sx={{ fontSize: 14 }} />
                    PDF
                  </button>
                </div>
              </div>

              <div className="space-y-12">
                {groups.map((group) => (
                  <section key={group.name}>
                    {/* Group header — matches the PDF "BAHAR EV – 18.25" pattern */}
                    <div className="flex items-baseline justify-between mb-4 pb-3 border-b border-hairline">
                      <h3 className="font-mono text-[12px] md:text-[14px] tracking-[0.2em] uppercase font-bold text-accent">
                        {group.name}
                      </h3>
                      <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-ink-dim tabular-nums">
                        {group.pageTotal.toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1')} {language === 'tr' ? 'SAYFA' : 'PG'} · {group.rows.length} {language === 'tr' ? 'SAHNE' : 'SC'}
                      </span>
                    </div>

                    <div className="overflow-x-auto border border-hairline">
                      <table className="w-full min-w-[1200px]">
                        <thead className="bg-elevated border-b border-hairline">
                          <tr>
                            {visibleHeaders.map((h, i) => (
                              <th
                                key={i}
                                className="px-3 py-3 text-left font-mono text-[9px] tracking-[0.16em] uppercase text-ink-mute font-semibold whitespace-nowrap"
                              >
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {group.rows.map((row, ri) => (
                            <tr
                              key={ri}
                              className="border-b border-hairline last:border-b-0 hover:bg-accent-soft transition-colors align-top"
                            >
                              {visibleHeaderIdxs.map((origIdx, vi) => {
                                const cell = row[origIdx] ?? '';
                                const isSceneNo = vi === sceneNoColLocalIdx;
                                return (
                                  <td
                                    key={vi}
                                    className={`px-3 py-3 font-mono text-[11px] whitespace-pre-wrap leading-snug ${
                                      isSceneNo
                                        ? 'text-accent font-bold tabular-nums whitespace-nowrap'
                                        : 'text-ink-dim'
                                    }`}
                                  >
                                    {cell || '—'}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                ))}
              </div>

              {!demoMode && (
                <div className="mt-10 flex justify-end">
                  <button
                    onClick={async () => {
                      if (!window.confirm('Mekan dökümü yeniden oluşturulacak. Devam etmek istiyor musunuz?')) return;
                      await handleGenerateLocationBreakdown();
                    }}
                    disabled={generatingLocation}
                    className="font-mono text-[11px] tracking-[0.18em] uppercase text-ink-dim hover:text-accent transition-colors disabled:opacity-50"
                  >
                    {generatingLocation ? (language === 'tr' ? 'OLUŞTURULUYOR…' : 'GENERATING…') : (language === 'tr' ? '↻ YENİDEN OLUŞTUR' : '↻ REGENERATE')}
                  </button>
                </div>
              )}
            </div>
          );
        }

        // Old summary-style TSV (one row per unique location, no group column)
        // — kept for projects whose breakdown was generated before this format
        // change. The "Yeniden Oluştur" button below upgrades them.
        return (
          <div className="w-full">
            <div className="flex items-end justify-between mb-12 pb-6 border-b border-hairline">
              <div>
                <span className="eyebrow mb-3 block">/ {language === 'tr' ? 'MEKANLAR' : 'LOCATIONS'}</span>
                <h2 className="display-h display-md">
                  {language === 'tr' ? <>{locRows.length} <em>mekan.</em></> : <>{locRows.length} <em>locations.</em></>}
                </h2>
              </div>
              <button
                onClick={() => exportToExcel(locExcelData, `mekan_dokumu_${safeFilename}`, 'Mekan Dökümü')}
                className="btn-accent"
              >
                <GetApp sx={{ fontSize: 14 }} />
                {t.exportExcel?.toUpperCase()}
              </button>
            </div>
            {!demoMode && (
              <div className="mb-6 px-5 py-4 border border-signal-yellow/40 bg-signal-yellow/10 flex items-center justify-between gap-4">
                <div>
                  <span className="eyebrow-mute mb-1 block text-signal-yellow">/ NOTICE</span>
                  <p className="font-prose text-[13px] text-ink-dim">
                    {language === 'tr'
                      ? 'Eski özet formatı. Yeni sahne-bazlı tabloya geçmek için yeniden oluştur.'
                      : 'Old summary format. Regenerate to upgrade to the scene-by-scene table.'}
                  </p>
                </div>
                <button
                  onClick={async () => {
                    if (!window.confirm('Yeniden oluşturulsun mu?')) return;
                    await handleGenerateLocationBreakdown();
                  }}
                  disabled={generatingLocation}
                  className="btn-accent !px-3 !py-2 !text-[10px] disabled:opacity-50 shrink-0"
                >
                  {generatingLocation ? '...' : (language === 'tr' ? '↻ YENİDEN' : '↻ REGEN')}
                </button>
              </div>
            )}
            <div className="overflow-x-auto border border-hairline w-full">
              <table className="w-full min-w-[880px]">
                <thead className="bg-elevated border-b border-hairline">
                  <tr>
                    {locHeaders.map((h, i) => (
                      <th key={i} className="px-4 py-4 text-left font-mono text-[10px] tracking-[0.18em] uppercase text-accent font-bold whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {locRows.map((row, ri) => (
                    <tr key={ri} className="border-b border-hairline last:border-b-0 hover:bg-accent-soft transition-colors">
                      {row.map((cell, ci) => (
                        <td
                          key={ci}
                          className={`px-4 py-4 font-mono text-[12px] whitespace-pre-wrap min-w-[10rem] tabular-nums ${
                            ci === 0 ? 'text-accent font-bold' : 'text-ink-dim'
                          }`}
                        >
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      }

      // Fallback: legacy markdown content (older breakdowns generated before TSV format)
      return (
        <div className="w-full">
          <div className="mb-12 pb-6 border-b border-hairline">
            <span className="eyebrow mb-3 block">/ {language === 'tr' ? 'MEKANLAR' : 'LOCATIONS'}</span>
            <h2 className="display-h display-md">
              {language === 'tr' ? <>Detaylı <em>mekan dökümü.</em></> : <>Detailed <em>location breakdown.</em></>}
            </h2>
          </div>
          {!demoMode && (
            <div className="mb-8 px-5 py-4 border border-signal-yellow/40 bg-signal-yellow/10 flex items-center justify-between gap-4">
              <div>
                <span className="eyebrow-mute mb-1 block text-signal-yellow">/ NOTICE</span>
                <p className="font-prose text-[14px] text-ink-dim leading-relaxed">
                  {language === 'tr' ? 'Bu döküm eski formatta. Tablo görünümü için yeniden oluştur.' : 'This breakdown is in legacy format. Regenerate for the table view.'}
                </p>
              </div>
              <button
                onClick={handleGenerateLocationBreakdown}
                disabled={generatingLocation}
                className="btn-accent !px-3 !py-2 !text-[10px] disabled:opacity-50 shrink-0"
              >
                {generatingLocation ? (language === 'tr' ? 'OLUŞTURULUYOR…' : 'GENERATING…') : (language === 'tr' ? '↻ YENİDEN' : '↻ REGEN')}
              </button>
            </div>
          )}
          <div className="border border-hairline p-6 lg:p-10">
            <div
              className="analysis-content prose prose-sm dark:prose-invert max-w-none"
              dangerouslySetInnerHTML={{ __html: formatContentForDisplay(locationBreakdown) }}
            />
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-background transition-colors flex relative">
      <button
        onClick={() => setSidebarOpen(true)}
        aria-label={t.analysisSections}
        className="lg:hidden fixed top-[5rem] left-3 z-30 w-10 h-10 flex items-center justify-center bg-background border border-hairline text-ink hover:border-accent hover:text-accent transition-colors"
      >
        <MenuIcon sx={{ fontSize: 18 }} />
      </button>

      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-background/85 backdrop-blur-sm z-40"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      <div className={`w-80 max-w-[85vw] border-r border-hairline bg-background flex flex-col fixed left-0 top-[4.5rem] bottom-0 overflow-y-auto z-50 transform transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <button
          onClick={() => setSidebarOpen(false)}
          aria-label={t.close}
          className="lg:hidden absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-ink-mute hover:text-ink transition-colors z-10"
        >
          <Close sx={{ fontSize: 18 }} />
        </button>
        <div className="px-6 py-6 border-b border-hairline">
          <button
            onClick={() => (demoMode ? navigate('/demos') : navigate('/dashboard'))}
            className="font-mono text-[10px] tracking-[0.18em] uppercase text-ink-mute hover:text-accent transition-colors flex items-center gap-2 mb-8"
          >
            <ArrowBack sx={{ fontSize: 12 }} />
            {demoMode ? (language === 'tr' ? 'DEMOLAR' : 'DEMOS') : t.back?.toUpperCase()}
          </button>

          {/* Score */}
          <div className="flex items-start justify-between mb-6">
            <div>
              <span className="eyebrow-mute mb-2 block">{t.generalScore?.toUpperCase()}</span>
              <div className="flex items-baseline gap-2">
                <span className="font-serif italic text-6xl text-accent leading-none tabular-nums">
                  {(() => {
                    if (analysis?.score != null && analysis.score > 0) return analysis.score.toFixed(1);
                    if (project.score != null && project.score > 0) return project.score.toFixed(1);
                    const genelPuan = extractedPuanlama ? parseGenelPuan(extractedPuanlama) : null;
                    if (genelPuan !== null && genelPuan > 0) return genelPuan.toFixed(1);
                    return '—';
                  })()}
                </span>
                <span className="font-mono text-[11px] tracking-wider text-ink-mute">/10</span>
              </div>
            </div>
            <ThemeToggle size="sm" />
          </div>

          <h1 className="font-sans text-xl font-semibold leading-snug text-ink mb-2 break-words">
            {project.file_name}
          </h1>
          <span className="eyebrow-mute">
            {project.project_type?.toUpperCase()}
          </span>

          <div className="flex flex-col gap-2 mt-6">
            <div className="flex items-center gap-2">
              <button onClick={() => downloadPDF()} className="btn-accent flex-1 justify-center !px-3 !py-2.5">
                <GetApp sx={{ fontSize: 14 }} />
                PDF
              </button>
              <button
                className="w-10 h-10 flex items-center justify-center border border-hairline text-success hover:border-success transition-colors"
                aria-label="approved"
              >
                <CheckCircle sx={{ fontSize: 18 }} />
              </button>
            </div>
            {!demoMode && (
              <button
                onClick={handleDeleteProject}
                disabled={deleting}
                className="font-mono text-[10px] tracking-[0.18em] uppercase font-semibold text-signal-red hover:opacity-70 disabled:opacity-50 transition-opacity flex items-center justify-center gap-2 py-2 mt-1 border border-signal-red/30 hover:border-signal-red"
              >
                <Delete sx={{ fontSize: 12 }} />
                <span>{deleting ? (language === 'tr' ? 'SİLİNİYOR…' : 'DELETING…') : (language === 'tr' ? 'PROJEYİ SİL' : 'DELETE PROJECT')}</span>
              </button>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar">
          <div className="px-6 py-6">
            <span className="eyebrow mb-4 block">/ {t.analysisSections?.toUpperCase()}</span>

            <nav className="space-y-px">
              {(() => {
                const navBtn = (active: boolean) =>
                  `w-full text-left px-3 py-3 font-mono text-[11px] tracking-[0.18em] uppercase font-semibold transition-colors flex items-center gap-2 ${
                    active
                      ? 'text-accent bg-accent-soft border-l-2 border-accent pl-[10px]'
                      : 'text-ink-dim hover:text-accent hover:bg-accent-soft border-l-2 border-transparent pl-[10px]'
                  }`;
                const subBtn = (active: boolean) =>
                  `w-full text-left px-3 py-2 font-prose text-[13px] leading-snug transition-colors ${
                    active ? 'text-accent' : 'text-ink-dim hover:text-ink'
                  }`;
                const idx = (i: number) => (
                  <span className="font-mono text-[10px] text-ink-mute mr-1.5 tabular-nums shrink-0">
                    /{String(i).padStart(2, '0')}
                  </span>
                );
                let menuIdx = 0;
                const incIdx = () => ++menuIdx;
                return (
                  <>
                    <button onClick={() => handleMenuChange('overview')} className={navBtn(activeMenu === 'overview')}>
                      {idx(incIdx())}{t.overview}
                    </button>

                    {project.generate_report && (
                      <>
                        <button onClick={() => handleMenuChange('report')} className={navBtn(activeMenu === 'report')}>
                          {idx(incIdx())}{t.report}
                        </button>
                        {activeMenu === 'report' && analysis?.sections && Object.keys(analysis.sections).length > 0 && (
                          <div className="ml-6 mb-2 space-y-px border-l border-hairline pl-3">
                            {Object.keys(analysis.sections).map((sectionTitle) => {
                              const isActive = activeSection === sectionTitle;
                              return (
                                <button
                                  key={sectionTitle}
                                  onClick={() => {
                                    setActiveSection(sectionTitle);
                                    const element = sectionRefs.current[sectionTitle];
                                    if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                  }}
                                  className={subBtn(isActive)}
                                >
                                  <span className="font-serif italic">{sectionTitle}</span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </>
                    )}

                    {project.generate_report && extractedPuanlama && (
                      <button onClick={() => handleMenuChange('puanlama')} className={navBtn(activeMenu === 'puanlama')}>
                        {idx(incIdx())}{t.scoring}
                      </button>
                    )}

                    {isScript && project && (
                      <button onClick={() => handleMenuChange('characters')} className={navBtn(activeMenu === 'characters')}>
                        {idx(incIdx())}{t.characters}
                      </button>
                    )}

                    {isScript && project && (
                      <button onClick={() => handleMenuChange('sequential_breakdown')} className={navBtn(activeMenu === 'sequential_breakdown')}>
                        {idx(incIdx())}{t.sequentialBreakdown}
                      </button>
                    )}

                    {isScript && project && (
                      <button onClick={() => handleMenuChange('locations')} className={navBtn(activeMenu === 'locations')}>
                        {idx(incIdx())}{language === 'tr' ? 'Mekan Dökümü' : 'Location Breakdown'}
                      </button>
                    )}
                  </>
                );
              })()}
            </nav>
          </div>
        </div>
      </div>

      <div className={`flex-1 overflow-y-auto custom-scrollbar lg:ml-80 pt-32 lg:pt-20 ${theme === 'dark' ? 'bg-gray-950 report-reading-area' : ''}`}>
        <div className={`mx-auto px-5 sm:px-6 lg:px-12 py-6 lg:py-12 ${activeMenu === 'sequential_breakdown' ? 'max-w-[88rem]' : activeMenu === 'puanlama' ? 'max-w-6xl' : 'max-w-4xl'}`}>
          {renderContent()}
        </div>
      </div>

      {showProcessing && (
        <div className="fixed inset-0 bg-background/85 backdrop-blur-sm flex items-center justify-center z-40 p-4">
          <div className="bg-elevated border border-hairline px-8 py-7 max-w-sm w-full">
            <span className="eyebrow mb-3 block">/ {language === 'tr' ? 'İŞLENİYOR' : 'PROCESSING'}</span>
            <ul className="wave-menu mb-4">
              <li></li><li></li><li></li><li></li><li></li><li></li><li></li><li></li><li></li>
            </ul>
            <p className="font-prose text-[14px] text-ink-dim leading-relaxed text-center">
              {processingText || (language === 'tr' ? 'Analiz işlemi sürüyor — pencereyi kapatabilirsin.' : 'Analysis running — you can close this window.')}
            </p>
          </div>
        </div>
      )}

      {showSuccessModal.visible && (
        <div
          className="fixed inset-0 bg-background/85 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setShowSuccessModal(prev => ({ ...prev, visible: false }))}
        >
          <div
            className="bg-elevated border border-hairline px-8 py-7 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="eyebrow text-success mb-3 block">/ {language === 'tr' ? 'TAMAMLANDI' : 'COMPLETE'}</span>
            <h2 className="display-h text-3xl mb-3">
              {showSuccessModal.title || t.processCompleted}
            </h2>
            <p className="font-prose text-[14px] text-ink-dim leading-relaxed mb-8">
              {showSuccessModal.message || t.processCompletedMessage}
            </p>
            <div className="flex items-center gap-3">
              {showSuccessModal.targetMenu && (
                <button
                  onClick={() => {
                    const target = showSuccessModal.targetMenu!;
                    setShowSuccessModal(prev => ({ ...prev, visible: false }));
                    handleMenuChange(target);
                  }}
                  className="btn-accent flex-1 justify-center"
                >
                  {t.goToAnalysis}
                  <span aria-hidden>→</span>
                </button>
              )}
              <button
                onClick={() => setShowSuccessModal(prev => ({ ...prev, visible: false }))}
                className="btn-ghost"
              >
                {t.close}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default ProjectDetailPage;
